<template>
  <span
    class="tooltip"
    :class="{ 'is-visible': visible }"
    @mouseover="onEnter"
    @mouseleave="onLeave"
  >
    <slot
      name="icon"
      :visible="visible"
      :toggle="toggle"
    >
      <i
        class="icon pointer"
        :class="[ visible ? activeIcon : icon ]"
        @click.stop.prevent="toggle"
        @mouseover="onEnter"
        @mouseleave="onLeave"
      />
    </slot>
    <span
      class="tooltiptext"
      :style="{ '--tooltip-shift-x': shiftX + 'px' }"
      @touchstart.stop
      @click.stop
    >
      <span
        v-if="translatedTitle"
        class="tooltip-title"
      >{{ translatedTitle }}</span>
      <span
        v-if="isHtml"
        class="tooltip-html"
        v-html="translatedText"
      />
      <span
        v-else
        class="tooltip-content"
      >{{ translatedText }}</span>
    </span>
  </span>
</template>

<script>
export default {
  name: 'Tooltip',
  props: {
    text: {
      type: String,
      required: true
    },
    isHtml: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    translate: {
      type: Boolean,
      default: true
    },
    icon: {
      type: String,
      default: 'icon-info-new'
    },
    activeIcon: {
      type: String,
      default: 'icon-info-new-active'
    },
    closeDelayMs: {
      type: Number,
      default: 400
    }
  },
  data () {
    return {
      visible: false,
      shiftX: 0,
      closeTimeout: null
    }
  },
  computed: {
    translatedText () {
      return this.translate ? this.$t(this.text) : this.text
    },
    translatedTitle () {
      if (!this.title) return null
      return this.translate ? this.$t(this.title) : this.title
    }
  },
  beforeDestroy () {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  },
  methods: {
    onEnter () {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout)
        this.closeTimeout = null
      }
      this.visible = true
      this.shiftX = 0
      this.$nextTick(this.adjustPosition)
    },
    onLeave () {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout)
      }
      this.closeTimeout = setTimeout(() => {
        this.visible = false
        this.shiftX = 0
        this.closeTimeout = null
      }, this.closeDelayMs)
    },
    toggle () {
      this.visible = !this.visible
      if (this.visible) {
        this.$nextTick(this.adjustPosition)
      } else {
        this.shiftX = 0
      }
    },
    adjustPosition () {
      const el = this.$el && this.$el.querySelector('.tooltiptext')
      if (!el) return
      const rect = el.getBoundingClientRect()
      const padding = 16
      let shift = 0
      if (rect.left < padding) {
        shift = padding - rect.left
      } else if (rect.right > window.innerWidth - padding) {
        shift = (window.innerWidth - padding) - rect.right
      }
      this.shiftX = Math.round(shift)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/helpers/functions/border-radius-selectors';
@import '~theme/css/variables/grid';

.tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: zIndex(popup);
  vertical-align: middle;
}

.tooltip .tooltiptext {
  font-size: 14px;
  font-weight: 400;
  visibility: hidden;
  min-width: 260px;
  max-width: 320px;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 10px;
  padding: 10px 14px;
  position: absolute;
  z-index: zIndex(popup);
  bottom: 145%;
  left: 50%;
  transform: translateX(calc(-50% + var(--tooltip-shift-x, 0px)));
  line-height: 16px;
  box-shadow: 0 2px 20px 0 rgba(color(black), 0.22);
  display: flex;
  flex-direction: column;
}

.tooltip .tooltiptext .tooltip-title {
  font-weight: 700;
  margin-bottom: 4px;
  display: block;
}

.tooltip .tooltiptext .tooltip-content {
  display: block;
}

.tooltip .tooltiptext::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(calc(-50% - var(--tooltip-shift-x, 0px)));
  border-width: 6px;
  border-style: solid;
  border-color: black transparent transparent transparent;
  z-index: zIndex(popup);
}

.tooltip.is-visible .tooltiptext {
  visibility: visible;
  z-index: zIndex(popup);
}

.icon-info-new,
.icon-info-new-active {
  width: 17px;
  height: 14px;
  color: #949492;
  display: inline-block;
  line-height: 1;
}

.tooltip-html {
  color: #d0d0d0;
  font-size: 11px;
  line-height: 14px;
}

@include desktop {
  .icon-info-new,
  .icon-info-new-active {
    width: 20px;
    height: 17px;
  }
}
</style>
